import { useCallback } from "react";
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { userInfo } from "../../../store/selectors/user";
import { captureApiError } from "../../../lib/sentry-utils";
import { notificationAPI } from "../api/notificationAPI";
import { toNotification } from "../libs/notificationAdapter";
import { INotificationsPage } from "../types/Notification";

const PER_PAGE = 50;
const LIST_KEY = ["notifications"];
const UNREAD_KEY = ["notifications", "unread-count"];

/**
 * История уведомлений постранично. Только для авторизованных: без токена
 * бэкенд отвечает 401, а гостю показывать нечего.
 *
 * Новые пуши между запросами сдвигают страницы, поэтому записи склеиваем по
 * id — первое вхождение остаётся.
 */
export const useGetNotifications = () => {
  const { isAuth } = useSelector(userInfo);
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: LIST_KEY,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) =>
      (await notificationAPI.getPage(pageParam, PER_PAGE)).data.data,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
    enabled: isAuth,
    // Свежие пуши должны появляться при возврате на экран.
    refetchOnMount: "always",
  });

  const seen = new Set<number>();
  const notifications = (query.data?.pages ?? [])
    .flatMap((page) => page.items)
    .filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    })
    .map(toNotification);

  const { mutate: markRead } = useMutation({
    mutationFn: async (id: number) => (await notificationAPI.markRead(id)).data,
    // Маркер снимаем сразу, не дожидаясь ответа: тап и открытие шторки —
    // одно движение, мигать маркером после ответа сети нельзя.
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: LIST_KEY });
      queryClient.setQueryData<InfiniteData<INotificationsPage>>(
        LIST_KEY,
        (data) =>
          data && {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items.map((item) =>
                item.id === id ? { ...item, is_read: true } : item,
              ),
            })),
          },
      );
      queryClient.setQueryData<number>(UNREAD_KEY, (count) =>
        Math.max((count ?? 1) - 1, 0),
      );
    },
    onError: (error) => {
      captureApiError(error, "/client/notification/read", "POST");
      queryClient.invalidateQueries({ queryKey: LIST_KEY });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
    },
  });

  const markAsRead = useCallback(
    (id: number) => {
      const target = notifications.find((item) => item.id === id);
      if (!target || target.isRead) return;
      markRead(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markRead, query.data],
  );

  return {
    notifications,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    hasMore: Boolean(query.hasNextPage),
    loadMore: query.fetchNextPage,
    isLoadingMore: query.isFetchingNextPage,
    markAsRead,
  };
};

/** Счётчик для колокольчика в шапке — только непрочитанные текущего аккаунта. */
export const useUnreadNotificationsCount = () => {
  const { isAuth } = useSelector(userInfo);

  const { data = 0 } = useQuery({
    queryKey: UNREAD_KEY,
    queryFn: async () =>
      (await notificationAPI.getUnreadCount()).data.data.count,
    enabled: isAuth,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });

  return isAuth ? data : 0;
};
